import { useCallback, useMemo, useState, type ReactNode } from "react";

import {
  addMonths,
  compareDay,
  formatMonthYear,
  getWeekdayLabels,
  isSameDay,
  startOfDay,
} from "../dateUtils";
import { useCalendarMonth, type CalendarDayCell } from "../useCalendarMonth";
import { CalendarNav, type OverlayMode } from "./CalendarNav";
import { CalendarPane } from "./CalendarPane";
import { DayCell } from "./DayCell";
import { Header, LiveRegion, Panes, Root, ViewFade } from "./calendarStyles";
import { MonthYearOverlay } from "./MonthYearOverlay";
import { useCalendarKeyboard } from "./useCalendarKeyboard";
import type { CalendarProps } from "./types";

const YEAR_SPAN = 12; // 연도 뷰: 기준 ±12년

/**
 * 프레젠테이셔널 캘린더(RFC-date-picker) — **조합자**. 상태(overlay/slide 방향/hover)와 선택·키보드 배선만
 * 소유하고, 실제 UI는 하위 파츠에 위임한다: 헤더 `CalendarNav`(연/월 버튼), 한 달 `CalendarPane`,
 * 셀 `DayCell`, 드릴다운 `MonthYearOverlay`, 키보드/roving `useCalendarKeyboard`. `months=2`면 연속
 * 두 달을 함께 이동한다. 범위 부분 선택 중(start만)엔 hover한 셀까지 임시 밴드를 미리 보여준다.
 */
export const Calendar = ({
  viewDate,
  onViewDateChange,
  weekStartsOn,
  locale,
  min,
  max,
  isDateDisabled,
  selection,
  onSelectDate,
  initialFocusDate,
  months = 1,
  "aria-label": ariaLabel,
}: CalendarProps) => {
  const dual = months === 2;
  // 오버레이 뷰: 어느 pane(0/1)에서 월/연 중 무엇을 고르는 중인지. null이면 달력.
  const [overlay, setOverlay] = useState<{ pane: number; mode: OverlayMode } | null>(null);
  // 월 이동 방향(+1=다음→오른쪽에서 슬라이드, -1=이전→왼쪽에서). 그리드 slide-in용.
  const [slideDir, setSlideDir] = useState<1 | -1>(1);
  // 범위 hover 프리뷰용 hover 셀(부분 선택 중일 때만 사용).
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  // 월 이동 방향을 정하고 viewDate를 옮긴다(prev/next·키보드 공용).
  const goToMonth = useCallback(
    (target: Date, dir: 1 | -1) => {
      setSlideDir(dir);
      onViewDateChange(target);
    },
    [onViewDateChange],
  );

  const leftView = viewDate;
  const rightView = useMemo(() => addMonths(viewDate, 1), [viewDate]);
  const leftWeeks = useCalendarMonth({
    viewDate: leftView,
    weekStartsOn,
    min,
    max,
    isDateDisabled,
  });
  const rightWeeks = useCalendarMonth({
    viewDate: rightView,
    weekStartsOn,
    min,
    max,
    isDateDisabled,
  });

  const weekdayLabels = useMemo(
    () => getWeekdayLabels(weekStartsOn, locale),
    [weekStartsOn, locale],
  );
  const monthLabels = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, { month: "short" });
    return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(2021, m, 1)));
  }, [locale]);

  // 월 이동 시 스크린 리더에 알릴 라이브 리전 텍스트(포커스 이동 없이 라벨만 바뀌므로 필요).
  const liveLabel = useMemo(
    () =>
      dual
        ? `${formatMonthYear(leftView, locale)} – ${formatMonthYear(rightView, locale)}`
        : formatMonthYear(leftView, locale),
    [dual, leftView, rightView, locale],
  );

  const { focusedDate, setFocusedDate, onGridKeyDown, registerDayRef } = useCalendarKeyboard({
    initialFocusDate,
    weekStartsOn,
    dual,
    leftView,
    rightView,
    viewDate,
    goToMonth,
    onSelectDate,
  });

  // 오버레이 대상 pane의 표시 달(단일은 leftView).
  const paneViewOf = (pane: number) => (pane === 1 ? rightView : leftView);
  // pane이 (year, month)를 보이도록 viewDate 이동(두 달 함께: 오른쪽 pane이면 -1달).
  const applyPaneMonthYear = (pane: number, year: number, month: number) => {
    const paneDate = new Date(year, month, 1);
    onViewDateChange(pane === 1 ? addMonths(paneDate, -1) : paneDate);
  };

  const years = useMemo(() => {
    const base = paneViewOf(overlay?.pane ?? 0).getFullYear();
    const lo = min ? min.getFullYear() : base - YEAR_SPAN;
    const hi = max ? max.getFullYear() : base + YEAR_SPAN;
    const list: number[] = [];
    for (let y = lo; y <= hi; y++) list.push(y);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay, leftView, rightView, min, max]);

  // 연/월 버튼 → 자기 grid 토글. 같은 버튼 재클릭 시 닫힘(달력 복귀).
  const toggleOverlay = (pane: number, mode: OverlayMode) =>
    setOverlay((cur) => (cur && cur.pane === pane && cur.mode === mode ? null : { pane, mode }));

  // 범위 부분 선택(start만) 중이면 hover 셀까지 임시 밴드. anchor↔hover를 정렬해 lo/hi로 둔다.
  const anchor = selection.rangePreviewAnchor ?? null;
  const preview =
    anchor && hoveredDate
      ? compareDay(anchor, hoveredDate) <= 0
        ? { lo: anchor, hi: hoveredDate }
        : { lo: hoveredDate, hi: anchor }
      : null;
  const previewEnabled = anchor != null;

  // 셀 상태(endpoint/inRange) — 실제 선택 + (부분 선택 중) hover 프리뷰를 합친다.
  const cellState = (date: Date): { endpoint: boolean; inRange: boolean } => {
    let endpoint = selection.isEndpoint(date);
    let inRange = selection.isInRange(date);
    if (preview) {
      if (isSameDay(date, preview.lo) || isSameDay(date, preview.hi)) endpoint = true;
      else if (compareDay(date, preview.lo) > 0 && compareDay(date, preview.hi) < 0) inRange = true;
    }
    return { endpoint, inRange };
  };

  const renderCell = (cell: CalendarDayCell): ReactNode => {
    const { endpoint, inRange } = cellState(cell.date);
    return (
      <DayCell
        key={cell.date.getTime()}
        cell={cell}
        endpoint={endpoint}
        inRange={inRange}
        focusTarget={compareDay(cell.date, focusedDate) === 0}
        onSelect={onSelectDate}
        onFocus={(d) => setFocusedDate(startOfDay(d))}
        onHover={previewEnabled ? setHoveredDate : undefined}
        registerRef={registerDayRef}
      />
    );
  };

  if (overlay) {
    const pane = overlay.pane;
    const pv = paneViewOf(pane);
    return (
      <Root>
        <LiveRegion aria-live="polite">{liveLabel}</LiveRegion>
        {/* months↔years 전환도 remount로 fade-in(§5-C reduced-motion 가드는 ViewFade). */}
        <ViewFade key={overlay.mode}>
          {/* 오버레이에서도 연/월 버튼을 유지 — 열린 grid의 버튼이 ▲, 다른 버튼 클릭 시 바로 전환. */}
          <Header>
            <CalendarNav
              paneView={pv}
              locale={locale}
              monthsOpen={overlay.mode === "months"}
              yearsOpen={overlay.mode === "years"}
              onToggle={(mode) => toggleOverlay(pane, mode)}
            />
          </Header>
          <MonthYearOverlay
            mode={overlay.mode}
            paneView={pv}
            monthLabels={monthLabels}
            years={years}
            onPickMonth={(m) => {
              applyPaneMonthYear(pane, pv.getFullYear(), m);
              setOverlay(null); // 월 선택 → 달력 복귀
            }}
            onPickYear={(y) => {
              applyPaneMonthYear(pane, y, pv.getMonth());
              setOverlay(null); // 연도 선택 → 달력 복귀(월 재선택 강제하지 않음)
            }}
          />
        </ViewFade>
      </Root>
    );
  }

  const renderPane = (paneIndex: number, paneView: Date, weeks: CalendarDayCell[][]) => (
    <CalendarPane
      key={paneIndex}
      paneIndex={paneIndex}
      dual={dual}
      nav={
        // 여기(day 뷰)선 overlay가 항상 null — 열리면 위 overlay 분기가 렌더되므로 둘 다 닫힘.
        <CalendarNav
          paneView={paneView}
          locale={locale}
          monthsOpen={false}
          yearsOpen={false}
          onToggle={(mode) => toggleOverlay(paneIndex, mode)}
        />
      }
      onPrev={() => goToMonth(addMonths(viewDate, -1), -1)}
      onNext={() => goToMonth(addMonths(viewDate, 1), 1)}
      weeks={weeks}
      weekdayLabels={weekdayLabels}
      slideDir={slideDir}
      gridAriaLabel={paneIndex === 0 ? ariaLabel : formatMonthYear(paneView, locale)}
      monthKey={`${paneView.getFullYear()}-${paneView.getMonth()}`}
      onGridKeyDown={onGridKeyDown}
      renderCell={renderCell}
    />
  );

  return (
    <Root>
      <LiveRegion aria-live="polite">{liveLabel}</LiveRegion>
      {/* key="days"로 전환 시 remount되어 fade-in 재생. 팝업 밖으로 나가면 hover 프리뷰 해제. */}
      <ViewFade key="days">
        <Panes onMouseLeave={() => setHoveredDate(null)}>
          {renderPane(0, leftView, leftWeeks)}
          {dual ? renderPane(1, rightView, rightWeeks) : null}
        </Panes>
      </ViewFade>
    </Root>
  );
};
